import React from 'react';
import CW from '../../../assets/coverwhale_logo_white.png';
import Tooltip from 'components/Tooltip';

const EndorsementDetails = ({
  endorsementData,
  styles,
}) => {
  const formatChangeType = (type) => {
    return type.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="w-full md:w-1/2">
      <div
        className="mb-6 rounded-[12px] w-full mx-auto bg-white pb-6 max-w-screen-xl"
      >
        <header className="bg-black flex items-center justify-between px-4 pb-2 pt-2 rounded-t-[12px]">
          <img
            src={CW}
            alt="Cover Whale"
            className="w-[360px]"
          />
        </header>

        {/* Headers and endorsement information */}
        <div className="flex-col border rounded p-4 mt-4 pt-0 border-slate-300 mx-6">
          {endorsementData.endorsement_number && (
            <div className="flex justify-between py-2 pl-2 border-b border-slate-300">
              <div>Endorsement Number</div>
              <div>{endorsementData.endorsement_number}</div>
            </div>
          )}
          <div className="flex justify-between py-2 border-b pl-2 border-slate-300">
            <div>Submission Number</div>
            <div>{endorsementData.submission_number}</div>
          </div>
          <div className="flex justify-between py-2 pl-2 border-b border-slate-300">
            <div>Agency Name</div>
            <div>{endorsementData.account_name}</div>
          </div>
          <div className="flex justify-between py-2 pl-2 border-b border-slate-300">
            <div>Agency Email</div>
            <div>{endorsementData.agent_email}</div>
          </div>
          <div className="flex justify-between py-2 pl-2 border-b border-slate-300">
            <div>Insured Name</div>
            <div>{endorsementData.insured_name}</div>
          </div>
          <div className="flex justify-between py-2 pl-2 border-b border-slate-300">
            <div>Carrier</div>
            <div>{endorsementData.carrier}</div>
          </div>
          <div className="flex justify-between py-2 pl-2 border-slate-300">
            <div>Quote Request</div>
            <div>{endorsementData.quote_request ? 'Yes' : 'No'}</div>
          </div>

          {/* Display non-'other' type changes in the endorsement information section */}
          <div className='mt-2'>
            {endorsementData.changes.filter(
              change => change.type !== 'other' && change.type !== 'other_endorsement'
            ).map((change, index) => {
              try {
                const parsedValue = typeof change.value === 'string'
                  ? JSON.parse(change.value)
                  : change.value;

                // For array-type values (like removed drivers or vehicles)
                if (Array.isArray(parsedValue)) {
                  return (
                    <div key={`change-group-${index}`} className="mb-2">
                      <div className="py-2 bg-gray-100">
                        <div className="font-semibold text-gray-800 pl-2">
                          {formatChangeType(change.type)}
                        </div>
                      </div>

                      {parsedValue.map((item, itemIndex) => (
                        <div key={`item-${itemIndex}`} className="border-t border-slate-100 p-2">
                          {Object.entries(item).map(([key, val]) => (
                            <div
                              key={`${itemIndex}-${key}`}
                              className="flex justify-between py-1 pl-2"
                            >
                              <div className="text-gray-600">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div>
                                {typeof val === 'object' ? JSON.stringify(val) : val}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                }

                // For object-type values
                return (
                  <div key={`change-group-${index}`} className="mb-2">
                    <div className="py-2 bg-gray-100">
                      <div className="font-semibold text-gray-800 pl-2">
                        {formatChangeType(change.type)}
                      </div>
                    </div>

                    {Object.entries(parsedValue).map(([key, val]) => (
                      <div
                        key={`${index}-${key}`}
                        className="flex justify-between py-1 pl-2 border-t border-slate-100"
                      >
                        <div className="text-gray-600">
                          {key.replace(/_/g, ' ')}
                        </div>
                        <div>
                          {typeof val === 'object' ? JSON.stringify(val) : val}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              } catch (e) {
                return (
                  <div
                    key={`change-${index}`}
                    className="flex justify-between py-2 border-t border-slate-300"
                  >
                    <div>{formatChangeType(change.type)}</div>
                    <div>{change.value}</div>
                  </div>
                );
              }
            })}
          </div>
        </div>

        {/* Other Changes */}
        {endorsementData.changes.some(
          change => change.type === 'other' || change.type === 'other_endorsement'
        ) && (
          <>
            <div className="p-4 pl-6 mt-4 pt-0">
              Check a requested change and mark it as solved
            </div>

            <div className="flex-col border rounded px-2 pt-0 border-slate-300 mx-6">
              {endorsementData.changes.filter(
                change => change.type === 'other' || change.type === 'other_endorsement')
                .map((change, index, arr) => {
                  let displayValue = change.value;
                  let tooltipContent = null;

                  if (change.type !== 'other_endorsement') {
                    try {
                      const parsedValue = typeof change.value === 'string'
                        ? JSON.parse(change.value)
                        : change.value;

                      tooltipContent = (
                        <div className="text-sm">
                          {Object.entries(parsedValue).map(([key, val]) => (
                            <div key={key}>
                              <strong>{key.replace(/_/g, ' ')}:</strong> {typeof val === 'object' ? JSON.stringify(val) : val}
                            </div>
                          ))}
                        </div>
                      );

                      displayValue = `${formatChangeType(change.type)}`;
                    } catch (e) {
                      displayValue = change.value;
                    }
                  } else {
                    displayValue = change.value;
                  }

                  return (
                    <div
                      key={index}
                      className={`flex justify-between py-2 ${index === arr.length - 1 ? '' : 'border-b border-slate-300'}`}
                    >
                      <div className="inline-flex items-center">
                        {tooltipContent ? (
                          <Tooltip
                            text={tooltipContent}
                            bgColor="bg-black"
                            textColor="text-white"
                            position="right"
                          >
                            <span className="cursor-help border-b border-dotted border-gray-500">
                              {displayValue}
                            </span>
                          </Tooltip>
                        ) : (
                          <span>
                            {formatChangeType(change.type)}: {displayValue}
                          </span>
                        )}
                      </div>

                      {/* Checkbox for solve/unsolved 'other' type changes */}
                      <div className="inline-flex items-center">
                        <label className={styles.label}>
                          <span className={styles.checkbox_container}>
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={change.solved}
                              disabled
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={styles.icon}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="1"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                          </span>
                        </label>
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EndorsementDetails;
